/**
 * Mobile Team Setup
 * Clean, sharp mobile-specific UI for team configuration
 */

import React, { useState, useEffect } from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { leaguesAPI } from '../../../services/apiService';

const C = dynastyTheme.tokens.colors;

const MobileTeamSetup = ({ leagueId, userTeam, onTeamUpdated, user }) => {
  const [formData, setFormData] = useState({
    team_name: '',
    owner_name: '',
    team_logo_url: '',
    team_colors: {
      primary: '#eab308',
      secondary: '#1c1917'
    },
    team_motto: ''
  });
  
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Predefined color options - Compact for mobile
  const colorOptions = [
    { name: 'Gold', primary: '#eab308', secondary: '#1c1917' },
    { name: 'Red', primary: '#bd3039', secondary: '#132448' },
    { name: 'Navy', primary: '#132448', secondary: '#c4ced4' },
    { name: 'Blue', primary: '#005a9c', secondary: '#ffffff' },
    { name: 'Orange', primary: '#fd5a1e', secondary: '#27251f' },
    { name: 'Cardinal', primary: '#c41e3a', secondary: '#fedb00' },
    { name: 'Green', primary: '#003831', secondary: '#efb21e' },
    { name: 'Purple', primary: '#0a2351', secondary: '#b5985a' }
  ];

  // Load current team data
  useEffect(() => {
    console.log('MobileTeamSetup - userTeam:', userTeam);
    console.log('MobileTeamSetup - user:', user);
    
    if (userTeam) {
      const colors = userTeam.team_colors || formData.team_colors;
      setFormData({
        team_name: userTeam.team_name || '',
        owner_name: userTeam.owner_name || user?.given_name || user?.firstName || '',
        team_logo_url: userTeam.team_logo_url || '',
        team_colors: colors,
        team_motto: userTeam.team_motto || ''
      });
      
      console.log('MobileTeamSetup - formData set:', {
        team_name: userTeam.team_name,
        owner_name: userTeam.owner_name,
        user_given_name: user?.given_name
      });
      
      // Set CSS variables for team colors
      setTeamColorCSSVariables(colors);
      
      if (userTeam.team_logo_url) {
        setLogoPreview(userTeam.team_logo_url);
      }
    }
  }, [userTeam, user]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Helper function to set CSS variables for team colors
  const setTeamColorCSSVariables = (colors) => {
    if (!colors || !colors.primary) return;
    
    const root = document.documentElement;
    root.style.setProperty('--team-primary', colors.primary);
    root.style.setProperty('--team-secondary', colors.secondary || '#1c1917');
    
    // Set derived colors for hover/dark/light variations
    root.style.setProperty('--team-primary-hover', colors.primary);
    root.style.setProperty('--team-primary-dark', colors.primary);
    root.style.setProperty('--team-primary-light', `${colors.primary}1A`);
    root.style.setProperty('--team-primary-glow', `${colors.primary}0D`);
    root.style.setProperty('--team-primary-highlight', `${colors.primary}40`);

    console.log('🎨 MOBILE CSS variables set:', colors);
  };

  // Handle color selection
  const handleColorSelect = (colors) => {
    console.log('🎨 MOBILE handleColorSelect called with:', colors);
    console.log('🎨 MOBILE Extracting primary:', colors.primary, 'secondary:', colors.secondary);
    
    const teamColors = {
      primary: colors.primary,
      secondary: colors.secondary
    };
    
    setFormData(prev => {
      const updated = {
        ...prev,
        team_colors: teamColors
      };
      console.log('🎨 MOBILE Updated formData:', updated);
      return updated;
    });
    
    // Immediately update CSS variables for live preview
    setTeamColorCSSVariables(teamColors);
  };

  // Handle file selection and preview
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      
      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        setError('File size must be less than 2MB');
        return;
      }
      
      setLogoFile(file);
      setError('');
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Upload logo to S3
  const uploadLogo = async () => {
    if (!logoFile) return formData.team_logo_url;
    
    setIsUploading(true);
    try {
      const uploadResult = await leaguesAPI.uploadTeamLogo(leagueId, logoFile);
      
      if (uploadResult.success) {
        return uploadResult.logo_url;
      } else {
        throw new Error('Failed to upload logo');
      }
    } catch (err) {
      console.error('Logo upload failed:', err);
      throw new Error(`Failed to upload logo: ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setMessage('');
    
    try {
      // Upload logo if selected
      let logoUrl = formData.team_logo_url;
      if (logoFile) {
        logoUrl = await uploadLogo();
      }
      
      // Submit team setup
      const response = await leaguesAPI.setupTeam(leagueId, {
        team_name: formData.team_name,
        owner_name: formData.owner_name,
        team_logo_url: logoUrl,
        team_colors: formData.team_colors,
        team_motto: formData.team_motto
      });
      
      if (response.success) {
        setMessage('✓ Team setup updated!');
        
        // Update local form data
        setFormData(prev => ({
          ...prev,
          team_logo_url: logoUrl
        }));
        setLogoPreview(logoUrl);
        
        if (onTeamUpdated) {
          onTeamUpdated();
        }
        
        // Clear message after delay
        setTimeout(() => {
          setMessage('');
          setLogoFile(null);
        }, 3000);
      } else {
        setError(response.message || 'Failed to update team');
      }
    } catch (err) {
      console.error('Team setup error:', err);
      setError(err.response?.data?.detail || 'Failed to update team');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      width: '100%'
    }}>
      {/* Messages */}
      {error && (
        <div style={{
          backgroundColor: dynastyTheme.tokens.colors.error + '20',
          border: `1px solid ${dynastyTheme.tokens.colors.error}`,
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '15px',
          fontSize: '13px',
          color: dynastyTheme.tokens.colors.error,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}
      
      {message && (
        <div style={{
          backgroundColor: dynastyTheme.tokens.colors.success + '20',
          border: `1px solid ${dynastyTheme.tokens.colors.success}`,
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '15px',
          fontSize: '13px',
          color: dynastyTheme.tokens.colors.success,
          fontWeight: '600'
        }}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Team Name */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: '600',
            color: dynastyTheme.tokens.colors.neutral[300],
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Team Name *
          </label>
          <input
            type="text"
            value={formData.team_name}
            onChange={(e) => handleInputChange('team_name', e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              backgroundColor: dynastyTheme.tokens.colors.neutral[850],
              border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
              borderRadius: '6px',
              color: C.white,
              fontSize: '14px',
              fontWeight: '500'
            }}
            placeholder="Enter team name"
            required
            maxLength={50}
          />
        </div>

        {/* Manager Name */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: '600',
            color: dynastyTheme.tokens.colors.neutral[300],
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Manager Name *
          </label>
          <input
            type="text"
            value={formData.owner_name}
            onChange={(e) => handleInputChange('owner_name', e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              backgroundColor: dynastyTheme.tokens.colors.neutral[850],
              border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
              borderRadius: '6px',
              color: C.white,
              fontSize: '14px',
              fontWeight: '500'
            }}
            placeholder="Your name"
            required
            maxLength={100}
          />
        </div>

        {/* Team Logo */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: '600',
            color: dynastyTheme.tokens.colors.neutral[300],
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Team Logo
          </label>
          
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center'
          }}>
            {/* Logo Preview */}
            <div style={{
              width: '64px',
              height: '64px',
              backgroundColor: dynastyTheme.tokens.colors.neutral[850],
              border: `2px dashed ${dynastyTheme.tokens.colors.neutral[700]}`,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              flexShrink: 0
            }}>
              {logoPreview ? (
                <img 
                  src={logoPreview} 
                  alt="Logo preview" 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <span style={{
                  fontSize: '24px',
                  color: dynastyTheme.tokens.colors.neutral[600]
                }}>
                  📷
                </span>
              )}
            </div>

            {/* Upload Button */}
            <div style={{ flex: 1 }}>
              <input
                type="file"
                id="logo-upload"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <label
                htmlFor="logo-upload"
                style={{
                  display: 'inline-block',
                  padding: '8px 16px',
                  backgroundColor: dynastyTheme.tokens.colors.neutral[700],
                  color: dynastyTheme.tokens.colors.neutral[300],
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginBottom: '4px'
                }}
              >
                Choose File
              </label>
              <div style={{
                fontSize: '11px',
                color: dynastyTheme.tokens.colors.neutral[500]
              }}>
                PNG, JPG up to 2MB
              </div>
              {logoFile && (
                <div style={{
                  fontSize: '11px',
                  color: dynastyTheme.tokens.colors.success,
                  marginTop: '4px'
                }}>
                  ✓ {logoFile.name}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Team Colors */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: '600',
            color: dynastyTheme.tokens.colors.neutral[300],
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Team Colors
          </label>
          
          {/* Color Presets - Compact Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '6px',
            marginBottom: '12px'
          }}>
            {colorOptions.map((option, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleColorSelect(option)}
                style={{
                  padding: '8px',
                  backgroundColor: formData.team_colors.primary === option.primary
                    ? dynastyTheme.tokens.colors.primary + '20'
                    : dynastyTheme.tokens.colors.neutral[850],
                  border: `2px solid ${
                    formData.team_colors.primary === option.primary
                      ? dynastyTheme.tokens.colors.primary
                      : dynastyTheme.tokens.colors.neutral[700]
                  }`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <div style={{ display: 'flex', gap: '2px' }}>
                  <div 
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: option.primary,
                      border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`
                    }}
                  />
                  <div 
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: option.secondary,
                      border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`
                    }}
                  />
                </div>
                <span style={{
                  fontSize: '9px',
                  color: dynastyTheme.tokens.colors.neutral[400],
                  fontWeight: '600'
                }}>
                  {option.name}
                </span>
              </button>
            ))}
          </div>

          {/* Custom Color Inputs */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '8px'
          }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '10px',
                color: dynastyTheme.tokens.colors.neutral[500],
                marginBottom: '4px'
              }}>
                Primary
              </label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input
                  type="color"
                  value={formData.team_colors.primary}
                  onChange={(e) => handleColorSelect({
                    primary: e.target.value,
                    secondary: formData.team_colors.secondary
                  })}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '4px',
                    border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                    backgroundColor: 'transparent',
                    cursor: 'pointer'
                  }}
                />
                <input
                  type="text"
                  value={formData.team_colors.primary}
                  onChange={(e) => handleColorSelect({
                    primary: e.target.value,
                    secondary: formData.team_colors.secondary
                  })}
                  style={{
                    flex: 1,
                    padding: '6px 8px',
                    backgroundColor: dynastyTheme.tokens.colors.neutral[850],
                    border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                    borderRadius: '4px',
                    color: C.white,
                    fontSize: '11px'
                  }}
                />
              </div>
            </div>
            
            <div>
              <label style={{
                display: 'block',
                fontSize: '10px',
                color: dynastyTheme.tokens.colors.neutral[500],
                marginBottom: '4px'
              }}>
                Secondary
              </label>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input
                  type="color"
                  value={formData.team_colors.secondary}
                  onChange={(e) => handleColorSelect({
                    primary: formData.team_colors.primary,
                    secondary: e.target.value
                  })}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '4px',
                    border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                    backgroundColor: 'transparent',
                    cursor: 'pointer'
                  }}
                />
                <input
                  type="text"
                  value={formData.team_colors.secondary}
                  onChange={(e) => handleColorSelect({
                    primary: formData.team_colors.primary,
                    secondary: e.target.value
                  })}
                  style={{
                    flex: 1,
                    padding: '6px 8px',
                    backgroundColor: dynastyTheme.tokens.colors.neutral[850],
                    border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
                    borderRadius: '4px',
                    color: C.white,
                    fontSize: '11px'
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Color Preview */}
        <div style={{
          backgroundColor: dynastyTheme.tokens.colors.neutral[850],
          border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
          borderRadius: '6px',
          padding: '12px',
          marginBottom: '15px'
        }}>
          <div style={{
            fontSize: '10px',
            fontWeight: '600',
            color: dynastyTheme.tokens.colors.neutral[400],
            marginBottom: '8px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Preview
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <div 
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                fontWeight: '600',
                fontSize: '12px',
                backgroundColor: formData.team_colors.primary,
                color: formData.team_colors.secondary
              }}
            >
              {formData.team_name || 'Team Name'}
            </div>
            <div 
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                fontWeight: '600',
                fontSize: '12px',
                backgroundColor: formData.team_colors.secondary,
                color: formData.team_colors.primary,
                border: `2px solid ${formData.team_colors.primary}`
              }}
            >
              Secondary
            </div>
          </div>
        </div>

        {/* Team Motto */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '12px',
            fontWeight: '600',
            color: dynastyTheme.tokens.colors.neutral[300],
            marginBottom: '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Team Motto
          </label>
          <textarea
            value={formData.team_motto}
            onChange={(e) => handleInputChange('team_motto', e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              backgroundColor: dynastyTheme.tokens.colors.neutral[850],
              border: `1px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
              borderRadius: '6px',
              color: C.white,
              fontSize: '14px',
              minHeight: '80px',
              resize: 'vertical',
              fontFamily: 'inherit'
            }}
            placeholder="e.g., Champions are made here"
            maxLength={200}
          />
          <div style={{
            fontSize: '10px',
            color: dynastyTheme.tokens.colors.neutral[500],
            marginTop: '4px',
            textAlign: 'right'
          }}>
            {formData.team_motto?.length || 0}/200
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting || isUploading || !formData.team_name}
          style={{
            width: '100%',
            padding: '14px',
            backgroundColor: (isSubmitting || isUploading || !formData.team_name)
              ? dynastyTheme.tokens.colors.neutral[700]
              : dynastyTheme.tokens.colors.primary,
            color: (isSubmitting || isUploading || !formData.team_name)
              ? dynastyTheme.tokens.colors.neutral[500]
              : C.black,
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: '700',
            cursor: (isSubmitting || isUploading || !formData.team_name) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          {isSubmitting || isUploading ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid currentColor',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }} />
              <span>{isUploading ? 'Uploading...' : 'Saving...'}</span>
            </>
          ) : (
            <>
              <span>💾</span>
              <span>Save Team Setup</span>
            </>
          )}
        </button>
      </form>

      {/* Spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default MobileTeamSetup;
